import React from "react"
import usePublicCounters from "../hooks/usePublicCounters.js"
import TickingNumber from "../components/TickingNumber.jsx"
import CountersStrip from "../components/CountersStrip.jsx"
import Testimonials from "../components/Testimonials.jsx"
import WhatItDoes from "../components/WhatItDoes.jsx"
import HowItWorks from "../components/HowItWorks.jsx"
import Pricing from "../components/Pricing.jsx"
import Faq from "../components/Faq.jsx"
import { formatterFor, rawValueFor } from "../lib/format.js"

function Hero() {
  const { byName } = usePublicCounters()
  const headline = byName.get("additional_conversions")
  const totalVisits = byName.get("total_visits")
  const aiCalls = byName.get("ai_calls_total")

  return (
    <section className="max-w-6xl mx-auto px-6 pt-24 pb-32">
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-8">
        § 01 · Real numbers from real shops
      </p>

      <h1 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[40px] md:text-[52px] tracking-[-0.035em] leading-[1.05] max-w-3xl">
        AI that runs A/B tests on your Shopify store.
      </h1>

      {/* The live counter stays the visual centerpiece, but it is no longer
          the <h1> — a bare number carries no keyword signal for crawlers.
          Demoted to a styled <div> below the real headline. */}
      <div
        className="mt-10 font-sans font-medium text-[color:var(--color-ink-primary)] text-[120px] tracking-[-0.05em] leading-[0.92]"
        aria-label="Additional conversions caused by ShopShift's AI across every webshop running it"
      >
        <TickingNumber value={rawValueFor(headline)} format={formatterFor(headline?.unit)} />
      </div>
      <p className="mt-4 font-sans text-[24px] tracking-[-0.02em] text-[color:var(--color-ink-secondary)] max-w-2xl leading-[1.3]">
        additional conversions caused by ShopShift's AI, across every webshop running it.
      </p>

      <div className="mt-12 flex flex-wrap items-baseline gap-x-10 gap-y-4 font-mono text-[12px] tracking-[0.04em] text-[color:var(--color-ink-tertiary)]">
        <span>
          <TickingNumber value={rawValueFor(totalVisits)} format={formatterFor(totalVisits?.unit)} /> visits tracked
        </span>
        <span className="text-[color:var(--color-ink-faint)]">·</span>
        <span>
          <TickingNumber value={rawValueFor(aiCalls)} format={formatterFor(aiCalls?.unit)} /> AI calls run
        </span>
        <span className="text-[color:var(--color-ink-faint)]">·</span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-winner-dot)]"
            style={{ animation: "pulse 2s ease-in-out infinite" }}
          />
          Streaming
        </span>
      </div>

      <div className="mt-16 flex items-center gap-4 flex-wrap">
        <a
          href="/users/sign_up"
          className="inline-flex items-center bg-[color:var(--color-brand)] text-white font-sans text-[14px] font-medium px-5 py-3 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity"
        >
          Start your 30-day free trial
        </a>
        <a
          href="#how-it-works"
          className="font-sans text-[14px] font-medium text-[color:var(--color-ai)] hover:underline"
        >
          How it works →
        </a>
      </div>
      <p className="mt-4 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)]">
        Card required · no charge during trial · cancel any time
      </p>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </section>
  )
}

export default function HomeView({ priceCents = 9900 }) {
  return (
    <>
      <Hero />
      <CountersStrip />
      <Testimonials />
      <WhatItDoes />
      <HowItWorks />
      <Pricing priceCents={priceCents} />
      <Faq />
    </>
  )
}
